/**
 * Genesis Vintage Theme - JavaScript
 * Features: Scroll reveal, masonry layout, hover effects, accessibility
 */

class VintageTheme {
  constructor() {
    this.init();
  }

  init() {
    // Remove no-js class to enable JavaScript features
    document.documentElement.classList.remove('no-js');
    
    // Ensure all elements are visible by default
    document.querySelectorAll('.vintage-animate--hidden').forEach(el => {
      el.classList.remove('vintage-animate--hidden');
    });
    
    this.setupScrollReveal();
    this.setupMasonryLayout();
    this.setupHoverEffects();
    this.setupAccessibility();
    this.setupReducedMotion();
    this.setupMarquee();
    this.setupStickyHeader();
  }

  /**
   * Scroll Reveal Animation
   */
  setupScrollReveal(retryCount = 0) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      // Fallback: make all elements visible immediately
      document.querySelectorAll('.vintage-animate--hidden').forEach(el => {
        el.classList.remove('vintage-animate--hidden');
        el.classList.add('vintage-animate', 'vintage-animate--fade-in');
      });
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const element = entry.target;
          const animationType = element.dataset.animation || 'fade-in';
          
          element.classList.remove('vintage-animate--hidden');
          element.classList.add('vintage-animate', `vintage-animate--${animationType}`);
          observer.unobserve(element);
        }
      });
    }, observerOptions);

    // Observe elements with data-animation attribute
    const animatedElements = document.querySelectorAll('[data-animation]');
    animatedElements.forEach(el => {
      el.classList.add('vintage-animate--hidden');
      observer.observe(el);
    });

    // Auto-observe common vintage elements
    const vintageElements = document.querySelectorAll('.vintage-hero, .masonry-products, .editorial-split, .featured-categories');
    vintageElements.forEach(el => {
      if (!el.dataset.animation) {
        el.dataset.animation = 'fade-in';
        el.classList.add('vintage-animate--hidden');
        observer.observe(el);
      }
    });

    // If no elements found, they might not be in DOM yet - try again after a short delay
    if (animatedElements.length === 0 && vintageElements.length === 0 && retryCount < 5) {
      setTimeout(() => {
        this.setupScrollReveal(retryCount + 1);
      }, 100);
    }

    // Safety fallback: ensure all hidden elements become visible after 3 seconds
    setTimeout(() => {
      document.querySelectorAll('.vintage-animate--hidden').forEach(el => {
        el.classList.remove('vintage-animate--hidden');
        el.classList.add('vintage-animate', 'vintage-animate--fade-in');
      });
    }, 3000);

    // Additional fallback: check for elements every 500ms for the first 5 seconds
    let checkCount = 0;
    const checkInterval = setInterval(() => {
      checkCount++;
      if (checkCount >= 10) {
        clearInterval(checkInterval);
        return;
      }
      
      const hiddenElements = document.querySelectorAll('.vintage-animate--hidden');
      if (hiddenElements.length > 0) {
        hiddenElements.forEach(el => {
          el.classList.remove('vintage-animate--hidden');
          el.classList.add('vintage-animate', 'vintage-animate--fade-in');
        });
      }
    }, 500);

    // Store interval reference for cleanup
    this.checkInterval = checkInterval;
  }

  /**
   * Masonry Layout for Product Grids
   */
  setupMasonryLayout() {
    const masonryContainers = document.querySelectorAll('.masonry-products__grid');
    
    masonryContainers.forEach(container => {
      this.initMasonry(container);
    });

    // Recalculate on window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        masonryContainers.forEach(container => {
          this.initMasonry(container);
        });
      }, 250);
    });
  }

  initMasonry(container) {
    const items = container.querySelectorAll('.masonry-product-card');
    const columns = parseInt(container.dataset.columns) || 3;
    const mobileColumns = parseInt(container.dataset.mobileColumns) || 2;
    const gap = parseFloat(container.dataset.gap) || 1.5;
    
    // Use CSS Grid for simple masonry effect
    const isMobile = window.innerWidth <= 749;
    const currentColumns = isMobile ? mobileColumns : columns;
    
    container.style.gridTemplateColumns = `repeat(${currentColumns}, 1fr)`;
    container.style.gap = `${gap}rem`;
    
    // Add staggered animation to items
    items.forEach((item, index) => {
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        item.style.animationDelay = `${index * 0.1}s`;
        item.classList.add('vintage-animate--hidden', 'vintage-animate--scale-in');
        // Trigger animation immediately for masonry items
        setTimeout(() => {
          item.classList.remove('vintage-animate--hidden');
          item.classList.add('vintage-animate');
        }, index * 100);
      }
    });
  }

  /**
   * Enhanced Hover Effects
   */
  setupHoverEffects() {
    // Product card hover effects
    document.querySelectorAll('.masonry-product-card, .vintage-card').forEach(card => {
      const imageContainer = card.querySelector('.masonry-product-card__image-container, .vintage-card__image');
      const primaryImage = card.querySelector('.masonry-product-card__image--primary, .vintage-card__image img');
      const secondaryImage = card.querySelector('.masonry-product-card__image--secondary');
      
      if (imageContainer && primaryImage) {
        card.addEventListener('mouseenter', () => {
          if (secondaryImage) {
            primaryImage.style.opacity = '0';
            secondaryImage.style.opacity = '1';
          }
        });
        
        card.addEventListener('mouseleave', () => {
          if (secondaryImage) {
            primaryImage.style.opacity = '1';
            secondaryImage.style.opacity = '0';
          }
        });
      }
    });

    // Button hover effects
    document.querySelectorAll('.vintage-button').forEach(button => {
      button.addEventListener('mouseenter', () => {
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          button.style.transform = 'translateY(-2px)';
        }
      });
      
      button.addEventListener('mouseleave', () => {
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          button.style.transform = 'translateY(0)';
        }
      });
    });
  }

  /**
   * Accessibility Enhancements
   */
  setupAccessibility() {
    // Skip to content link
    const skipLink = document.querySelector('.skip-to-content-link');
    if (skipLink) {
      skipLink.addEventListener('click', (e) => {
        e.preventDefault();
        const target = document.querySelector(skipLink.getAttribute('href'));
        if (target) {
          target.focus();
          target.scrollIntoView({ behavior: 'smooth' });
        }
      });
    }

    // Focus management for modals and drawers
    this.setupFocusTrap();
    
    // Keyboard navigation for custom elements
    this.setupKeyboardNavigation();
    
    // Announce dynamic content changes
    this.setupLiveRegion();
  }

  setupFocusTrap() {
    const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    
    document.querySelectorAll('[data-focus-trap]').forEach(container => {
      const focusableContent = container.querySelectorAll(focusableElements);
      const firstFocusableElement = focusableContent[0];
      const lastFocusableElement = focusableContent[focusableContent.length - 1];

      container.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
              lastFocusableElement.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === lastFocusableElement) {
              firstFocusableElement.focus();
              e.preventDefault();
            }
          }
        }
      });
    });
  }

  setupKeyboardNavigation() {
    // Enhanced keyboard navigation for product cards
    document.querySelectorAll('.masonry-product-card, .vintage-card').forEach(card => {
      card.setAttribute('tabindex', '0');
      card.setAttribute('role', 'button');
      
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const link = card.querySelector('a');
          if (link) {
            link.click();
          }
        }
      });
    });
  }

  setupLiveRegion() {
    // Create live region for announcements
    if (!document.querySelector('#vintage-live-region')) {
      const liveRegion = document.createElement('div');
      liveRegion.id = 'vintage-live-region';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.className = 'visually-hidden';
      document.body.appendChild(liveRegion);
    }
  }

  announce(message) {
    const liveRegion = document.querySelector('#vintage-live-region');
    if (liveRegion) {
      liveRegion.textContent = message;
    }
  }

  /**
   * Reduced Motion Support
   */
  setupReducedMotion() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    const handleReducedMotion = (e) => {
      if (e.matches) {
        // Disable animations
        document.documentElement.style.setProperty('--transition-vintage-fast', '0s');
        document.documentElement.style.setProperty('--transition-vintage-normal', '0s');
        document.documentElement.style.setProperty('--transition-vintage-slow', '0s');
        
        // Remove animation classes and ensure visibility
        document.querySelectorAll('.vintage-animate, .vintage-animate--hidden').forEach(el => {
          el.classList.remove('vintage-animate', 'vintage-animate--hidden', 'vintage-animate--fade-in', 'vintage-animate--slide-in-left', 'vintage-animate--slide-in-right', 'vintage-animate--scale-in');
        });
      } else {
        // Re-enable animations
        document.documentElement.style.setProperty('--transition-vintage-fast', '0.15s ease-out');
        document.documentElement.style.setProperty('--transition-vintage-normal', '0.2s ease-out');
        document.documentElement.style.setProperty('--transition-vintage-slow', '0.3s ease-out');
      }
    };

    prefersReducedMotion.addEventListener('change', handleReducedMotion);
    handleReducedMotion(prefersReducedMotion);
  }

  /**
   * Sticky Header Functionality
   */
  setupStickyHeader() {
    const header = document.querySelector('.vintage-theme .header, .vintage-theme header-component');
    if (!header) return;

    let ticking = false;

    const updateHeader = () => {
      const scrollY = window.scrollY;
      
      if (scrollY > 20) {
        header.classList.add('header--sticky');
      } else {
        header.classList.remove('header--sticky');
      }
      
      ticking = false;
    };

    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(updateHeader);
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestTick, { passive: true });
    
    // Initial check
    updateHeader();
  }

  /**
   * Marquee Animation
   */
  setupMarquee() {
    const marquees = document.querySelectorAll('.vintage-marquee__track');
    
    marquees.forEach(track => {
      const speed = parseInt(track.dataset.speed) || 30;
      
      // Set CSS custom property for animation duration
      track.style.setProperty('--speed', `${speed}s`);
      
      // Pause on hover
      track.addEventListener('mouseenter', () => {
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          track.style.animationPlayState = 'paused';
        }
      });
      
      track.addEventListener('mouseleave', () => {
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          track.style.animationPlayState = 'running';
        }
      });
    });
  }

  /**
   * Cleanup method
   */
  destroy() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  }

  /**
   * Utility Methods
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  /**
   * Performance Optimization
   */
  setupPerformanceOptimizations() {
    // Lazy load images
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              imageObserver.unobserve(img);
            }
          }
        });
      });

      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    }

    // Preload critical resources
    this.preloadCriticalResources();
  }

  preloadCriticalResources() {
    // Preload hero images
    const heroImages = document.querySelectorAll('.vintage-hero__image');
    heroImages.forEach(img => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = img.src;
      document.head.appendChild(link);
    });
  }
}

/**
 * Initialize theme when DOM is ready
 */
let themeInitialized = false;

function initializeTheme() {
  if (!themeInitialized) {
    themeInitialized = true;
    new VintageTheme();
  }
}

document.addEventListener('DOMContentLoaded', initializeTheme);

/**
 * Fallback: ensure elements are visible if DOMContentLoaded has already fired
 */
if (document.readyState === 'loading') {
  // DOM is still loading, wait for DOMContentLoaded
} else {
  // DOM is already loaded, initialize immediately
  initializeTheme();
}

/**
 * Additional fallback: ensure elements are visible on window load
 */
window.addEventListener('load', () => {
  // Make sure all elements are visible after everything is loaded
  document.querySelectorAll('.vintage-animate--hidden').forEach(el => {
    el.classList.remove('vintage-animate--hidden');
    el.classList.add('vintage-animate', 'vintage-animate--fade-in');
  });
});

/**
 * Emergency fallback: ensure elements are visible after a delay
 */
setTimeout(() => {
  document.querySelectorAll('.vintage-animate--hidden').forEach(el => {
    el.classList.remove('vintage-animate--hidden');
    el.classList.add('vintage-animate', 'vintage-animate--fade-in');
  });
}, 1000);

/**
 * Final fallback: ensure elements are visible after 5 seconds
 */
setTimeout(() => {
  document.querySelectorAll('.vintage-animate--hidden').forEach(el => {
    el.classList.remove('vintage-animate--hidden');
    el.classList.add('vintage-animate', 'vintage-animate--fade-in');
  });
}, 5000);

/**
 * Ultimate fallback: ensure elements are visible after 10 seconds
 */
setTimeout(() => {
  document.querySelectorAll('.vintage-animate--hidden').forEach(el => {
    el.classList.remove('vintage-animate--hidden');
    el.classList.add('vintage-animate', 'vintage-animate--fade-in');
  });
}, 10000);

/**
 * Last resort fallback: ensure elements are visible after 30 seconds
 */
setTimeout(() => {
  document.querySelectorAll('.vintage-animate--hidden').forEach(el => {
    el.classList.remove('vintage-animate--hidden');
    el.classList.add('vintage-animate', 'vintage-animate--fade-in');
  });
}, 30000);

/**
 * Export for use in other scripts
 */
window.VintageTheme = VintageTheme;
